<template>
  <div class="wt-container h-rest">
    <div>
      <p class="introduction-text">
        Welcome to the Water Footprint Assessment Tool. This tool will help you calculate and map the water footprint, assess its sustainability, and identify strategic actions to improve the sustainability, efficiency and equitability of water use.
      </p>
      <div class="block">
        <p>
          The Water Footprint Assessment Tool is developed by <a href="https://www.waterfootprintimplementation.com/">Water Footprint Implementation</a>, in collaboration with the <a href="https://waterfootprint.org/en/">Water Footprint Network</a>. Bringing together the internationally recognized <a href="https://waterfootprint.org/en/water-footprint/global-water-footprint-standard/">Global Water Footprint Standard</a> and the most comprehensive, global water footprint database – <a href="https://waterfootprint.org/en/resources/waterstat/">WaterStat</a>, the Water Footprint Assessment Tool allows you to complete the geographic assessment options below, and provides results based on sound methods and best available data. Production assessment options will be featured in a next version of this tool. 
        </p>
<!--
        <div style="margin-bottom: 20px; padding: 25px; border-radius: 15px; background: #e45a00; font-weight: bold">
          <h4 style="color: white; font-size: 25px">Unlock New Tool Functions!</h4>
          <p style="margin-bottom: 34px; color: white">
            We have launched a <a href="https://www.indiegogo.com/projects/the-water-footprint-assessment-tool" style="color: white">crowdfunding campaign on Indiegogo</a> to unlock water footprint production assessment in our FREE online tool. The Water Footprint Assessment Tool is positioned to make even more valuable contributions to smart, efficient and equitable water use around the world!
            <br/><br/>
            What are the new functions we are seeking to unlock?
            <br/><br/>
            <ul>
              <li>Raw Product: To allow users to select and obtain insights into the water footprint of a raw product such as specific crops, main metals and minerals.</li>
              <li>My Commodity (A): Users will be able to perform a water footprint analysis on an agricultural-based commodity.</li>
              <li>My Commodity (B): Metal, mineral and biofuel-based commodities will be added to the tool.</li>
              <li>My Business: Businesses that operate in specific sectors such as agri-food, textile, and mining, will be able to calculate their water footprint by analyzing their operations and supply
chains.</li>
            </ul>
          </p>
          <a class="button" href="https://www.indiegogo.com/projects/the-water-footprint-assessment-tool">Visit our campaign page!</a>
        </div>
-->
        <div class="text-right">
          <a href="#" @click="toggleMoreInfo()">{{moreInfo? "&lt;&lt;": "&gt;&gt;"}} contact & credits</a>
        </div>
        <div v-if="moreInfo">
          <h4>Contact</h4>
          <p>
            For practical applications: <a href="mailto:info@waterfootprintimplementation.com">info@waterfootprintimplementation.com</a><br/>
            For community and academic inquiries: <a href="mailto:info@waterfootprint.org">info@waterfootprint.org</a>
          </p>
          <h4>Copyright (2020)</h4>
          <p>Water Footprint Implementation, Water Footprint Network, Arjen Hoekstra & Mesfin Mekonnen</p>
          <h4>Development Team</h4>
          <p>Michiel van Heek, Istvan Baro, Csaba Bigyi</p>
          <h4>Legacy</h4>
          <p>The old version of the Water Footprint Assessment Tool is <a href="assessment">still available</a> (requires Flash Player).</p>
        </div>
      </div>
    </div>
    <div class="box-ct">
      <div class="title">Assess<span> the water footprint of:</span></div>
      <div class="d-flex flex-wrap">
        <div class="h-card" @click="showCountryDialog()">
          <div class="box">
            <img src="../assets/img/icons/country.png" class="h-card--icon" alt />
            <span class="box__title">A Country</span>
            <div class="mask__ct">
              <div class="mask mask--1"></div>
              <div class="mask mask--2"></div>
              <div class="mask mask--3"></div>
            </div>
          </div>
        </div>
        <div class="h-card" @click="showRiverBasinDialog()">
          <div class="box">
            <img src="../assets/img/icons/river-basin.png" class="h-card--icon" alt />
            <span class="box__title">A River Basin</span>
            <div class="mask__ct">
              <div class="mask mask--1"></div>
              <div class="mask mask--2"></div>
              <div class="mask mask--3"></div>
            </div>
          </div>
        </div>
        <div class="h-card" @click="goNext()">
          <div class="box">
            <img src="../assets/img/icons/world.png" class="h-card--icon" alt />
            <span class="box__title">The World</span>
            <div class="mask__ct">
              <div class="mask mask--1"></div>
              <div class="mask mask--2"></div>
              <div class="mask mask--3"></div>
            </div>
          </div>
        </div>
        <div class="h-card">
<!--          <a class="donate" href="https://www.indiegogo.com/projects/the-water-footprint-assessment-tool" target="_blank">
            <img src="../assets/img/donate.png">Donate to unlock</a> -->
          <div class="box disabled">
            <img src="../assets/img/icons/raw-product_disabled.png" class="h-card--icon" alt />
            <span class="box__title">A Raw Product</span>
            <div class="mask__ct">
              <div class="mask mask--disabled"></div>
            </div>
          </div>
        </div>
        <div class="h-card">
<!--          <a class="donate" href="https://www.indiegogo.com/projects/the-water-footprint-assessment-tool" target="_blank">
            <img src="../assets/img/donate.png">Donate to unlock</a> -->
          <div class="box disabled">
            <img src="../assets/img/icons/business_disabled.png" class="h-card--icon" alt />
            <span class="box__title">My Business</span>
            <div class="mask__ct">
              <div class="mask mask--disabled"></div>
            </div>
          </div>
        </div>
        <div class="h-card">
<!--          <a class="donate" href="https://www.indiegogo.com/projects/the-water-footprint-assessment-tool" target="_blank">
            <img src="../assets/img/donate.png">Donate to unlock</a> -->
          <div class="box disabled">
            <img src="../assets/img/icons/product_disabled.png" class="h-card--icon" alt />
            <span class="box__title">My Commodity</span>
            <div class="mask__ct">
              <div class="mask mask--disabled"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <modals-container />
  </div>
</template>

<script>
import Country from "@/components/modal/Country.vue";
import RiverBasin from "@/components/modal/RiverBasin.vue";
import { assessment, assessmentTypes } from "@/services/assessment-model.js";

export default {
  name: "Home",
  data() {
    return {
      moreInfo: false
    }
  },
  beforeDestroy() {
    this.$root.$off("modal-event");
  },
  methods: {
    toggleMoreInfo() {
      this.moreInfo = !this.moreInfo;
    },
    showCountryDialog: function() {
      assessment.setAssessmentType(assessmentTypes.countries);
      this.$modal.show(Country, { draggable: true }, { height: "500px" });
    },
    showRiverBasinDialog: function() {
      assessment.setAssessmentType(assessmentTypes.basins);
      this.$modal.show(RiverBasin, { draggable: true }, { height: "500px" });
    },
    goNext: function() {
      assessment.setAssessmentType(assessmentTypes.world);
      assessment.nextStep();
    }
  }
};
</script>

<style lang="scss" scoped>
.block {
  h4 {
    margin: 0 0 15px 0;
    padding: 0;
    color: #009BD1;
    font-size: 1em;
    font-weight: normal;
    font-family: "rubrikregular";
  }
  p {
    margin: 0 0 30px 0;
    padding: 0;
    color: #301948;
    font-size: 15px;
    line-height: 23px;
  }
  a {
    color: #301948;
    font-size: 15px;
    text-decoration: underline;
  }
  a:active, a:hover {
    outline: 0;
  }
  a:hover {
    color: #41b883;
  }
  a.button {
    color: white;
    text-decoration: none;
  }
}

.box-ct {
  margin-top: 100px;
  position: relative;
  border: 24px solid #009bd1;
  border-radius: 50px;
  padding: 30px;

  @media (max-width: 1024px){
    border: 16px solid #009bd1;
  }
}

.title {
  background: white;
  font-size: 30px;
  color: #009bd1;
  line-height: 30px;
  padding: 0 20px;
  position: absolute;
  top: -26px;
  left: 200px;
  white-space: nowrap;

  @media (max-width: 1440px){
    left: 100px;
  }

  @media (max-width: 1024px){
    font-size: 22px;
    left: 50%;
    top: -22px;
    transform: translateX(-50%);
  }

  @media (max-width: 600px){
    span {
      display: none;
    }
  }
}

.h-card {
  cursor: pointer;
  position: relative;
  flex: 1;

  @media (max-width: 1440px){
    flex: 33.33%;
    max-width: 33.33%;
  }

  @media (max-width: 1024px){
    flex: 50%;
    max-width: 50%;
  }

  @media (max-width: 600px){
    flex: 100%;
    max-width: 100%;
  }

  &:hover {
    .box {
      box-shadow: 5px 5px 20px 0 rgba(0, 0, 0, 0.24);
    }
  }

  .box {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 80%;
    width: 80%;
    margin: 10% auto;
    font-size: 24px;
    border-radius: 25px;
    overflow: hidden;
    transition: all 0.4s;

    @media (max-width: 1640px) {
      font-size: 20px
    }

    &.disabled {
      opacity: 0.5;
      box-shadow: none;
    }
    &__title {
      color: white;
      width: 100%;
      text-align: center;
      position: absolute;
      bottom: 5%;
      z-index: 4;
    }

  }

  .donate {
    position: absolute;
    top: 0;
    left: 10%;
    background-color: #e45a00;
    display: flex;
    align-items: center;
    flex-direction: row;
    padding: 10px 20px 10px 10px;
    border-bottom-right-radius: 25px;
    border-top-left-radius: 25px;
    font-size: 12px;
    font-weight: bold;
    color: white;
    z-index: 10;
    text-decoration: none;
    &:hover {
      background-color: #41b883;
    }

    img {
      margin-right: 10px;
      width: 25px;
    }
  }

  &--icon {
    position: absolute;
    top: 10%;
    width: 75%;
  }

  .mask {
    position: absolute;
    left: 0;
    width: 800%;
    height: 100%;
    background-repeat: repeat no-repeat;
    background-position: left bottom;
    background-size: 100% 60%;
    transform-origin: left bottom;

    &__ct {
      position: absolute;
      width: 100%;
      overflow: hidden;
      height: 100%;
      bottom: 0;
    }

    &--1 {
      background-image: url("../assets/img/shape1_new.png");
      z-index: 3;
    }
    &--2 {
      background-image: url("../assets/img/shape2_new.png");
      z-index: 2;
    }
    &--3 {
      background-image: url("../assets/img/shape3_new.png");
      z-index: 1;
    }
    &--disabled {
      background-image: url("../assets/img/shape_disabled.png");
    }
  }
  &:hover {
    .mask {
      animation: waves 10s linear infinite;
      &--disabled {
        animation: none;
      }
    }
  }
}

@keyframes waves {
  100% {
    transform: translateX(-50%) translateZ(0) scaleY(1);
    left: 0;
  }
  50% {
    transform: translateX(-25%) translateZ(0) scaleY(0.85);
  }
  0% {
    transform: translateX(0) translateZ(0) scaleY(1);
  }
}
</style>
